import {last} from 'lodash';
import {
  Component,
  Expressions,
  Plugins,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  EElement,
  EList,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  copyInstanceSuffix,
  getGuidNullable,
  type NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  CHILDREN_PATH,
  getComponentInstRootElement,
  getPageComponentFromComponentMap,
  pageComponentType,
  removeElementById,
  type RelativePositionPlacementOptions,
} from '@packages/utilities/core-elements';
import {isSlot} from '@packages/utilities/elementTypeUtils';
import {memoize, weakMemo} from '@packages/utilities/memo';
// Direct-file import to skip barrels that would re-enter platform mid-load.
// eslint-disable-next-line webflow/module-top-level-imports
import {
  getInnermostSymbolInstance,
  getTargetElementOrInstance,
} from '@packages/utilities/native-id-paths/NativeIdPathUtils';
import {
  applySlotDefaultWrite,
  normalizeSlotDefaultChain,
  resolveSlotDefaultWriteChain,
  type SlotDefaultLevel,
} from './SlotDefaultWriteUtils';
import {
  getEffectiveSlotDefaultExpression,
  getTargetedSlotFieldId,
  resolveSlotDefaultPath,
  resolveSlotDefaultPathContext,
} from './SlotDefaultUtils';
import {getComponentSlots} from './SlotElementUtils';

export type SlotDefaultMoveSource = {
  element: ExpressionElement;
  nativeIdPath: NativeId[];
};

type AnchorDestination =
  | {
      kind: 'slotDefault';
      componentType: Plugins.QualifiedElement;
      fieldId: string;
      expression: Expression;
    }
  | {kind: 'componentRender'; componentType: Plugins.QualifiedElement};

export type PromotedSlotDefaultMove = {
  componentMap: Component.ComponentMap;
  sourceElementId: string;
  sourceNativeIdPath: NativeId[] | null;
  // Null when the promotion did not touch the element — an ordinary live source
  // stays the caller's to resolve. A promoted one is always lifted out of its
  // default, so it is always this one.
  detachedSourceElement: ExpressionElement | null;
  anchorElement: ExpressionElement | null;
  anchorElementId: string;
  anchorNativeIdPath: NativeId[] | null;
  // Where a promoted anchor's write lands. A promotion produces one or the
  // other, never both: a slot default to write through, or the render of the
  // component that now owns the anchor.
  anchorDestination: AnchorDestination | null;
  // Where the element lands inside `anchorDestination`'s expression. Null when
  // the write does not go through one.
  insertion: SlotDefaultInsertion | null;
};

type SlotDefaultMoveInput = {
  componentMap: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  sourceElementId: string;
  sourceNativeIdPath: NativeId[] | null;
  anchorElementId: string;
  anchorNativeIdPath: NativeId[] | null;
  anchorPath: ExpressionPath | null;
  anchorPosition?: RelativePositionPlacementOptions | null;
  editingComponentType?: Plugins.QualifiedElement | null;
};

type SlotDefaultMovePreparation = 'preview' | 'commit';

const isOwnedPath = (
  nativeIdPath: NativeId[],
  ownerInstancePath: NativeId[]
): boolean =>
  ownerInstancePath.every(
    (nativeId, index) =>
      getGuidNullable(nativeIdPath[index]) === getGuidNullable(nativeId)
  );

const remapPath = (
  nativeIdPath: NativeId[],
  ownerInstancePath: NativeId[],
  idMap: Record<string, string>,
  forkedExpression: Expression
): NativeId[] => {
  if (!isOwnedPath(nativeIdPath, ownerInstancePath)) return nativeIdPath;

  // Two Slots on one component project the same default, so a sibling tree
  // hanging off this same owner holds the very same authored ids. The fork only
  // owns the tree it made — once a segment is neither reminted nor sitting in
  // that fork, the path has left it and nothing past it is ours to rewrite.
  // A level authored in the definition being edited is not reminted at all, so
  // being absent from the id map is on its own no reason to stop.
  let insideFork = true;
  return nativeIdPath.map((nativeId, index) => {
    if (index < ownerInstancePath.length) return nativeId;
    if (!insideFork) return nativeId;

    const authoredId = getGuidNullable(nativeId) ?? '';
    const reassignedId = idMap[authoredId];
    if (reassignedId) return copyInstanceSuffix(nativeId)(reassignedId);

    if (!Expressions.getElementById(forkedExpression, authoredId)) {
      insideFork = false;
    }
    return nativeId;
  });
};

/**
 * An element and the path it was reached by.
 *
 * The two travel together because a promotion remints ids, and a sibling
 * projected tree holds the very same authored ids — an id on its own cannot say
 * which tree it belongs to, so letting the pair drift apart silently retargets
 * the move. A null path means the element is not reachable by one: it has been
 * lifted out of its default and is travelling detached.
 */
type PlacedElement = {
  id: string;
  nativeIdPath: NativeId[] | null;
};

/**
 * Moves a placed element onto the fork that was just written.
 *
 * The path is remapped first and the id is then re-read off it, so the id can
 * only ever name whatever the path now points at.
 */
const remapPlaced = (
  placed: PlacedElement,
  ownerInstancePath: NativeId[],
  idMap: Record<string, string>,
  forkedExpression: Expression
): PlacedElement => {
  // Without a path there is nothing to say this element is even in the fork,
  // and a bare id lookup cannot tell: a sibling projected tree carries the same
  // authored ids, so the fork would happily answer for its twin.
  if (!placed.nativeIdPath) return placed;

  const nativeIdPath = remapPath(
    placed.nativeIdPath,
    ownerInstancePath,
    idMap,
    forkedExpression
  );
  const index = placed.nativeIdPath.findIndex(
    (nativeId) => getGuidNullable(nativeId) === placed.id
  );
  const id =
    index < 0 ? placed.id : (getGuidNullable(nativeIdPath[index]) ?? placed.id);

  return {id, nativeIdPath};
};

const getOwningComponentType = (
  componentMap: Component.ComponentMap,
  nativeIdPath: NativeId[]
): ExpressionElement['type'] | null => {
  const pageComponent = getPageComponentFromComponentMap(componentMap);
  if (!pageComponent) return null;

  let componentType = pageComponentType;
  let render = Component.getRender(pageComponent);
  for (let index = 0; index < nativeIdPath.length; index++) {
    const elementId = getGuidNullable(nativeIdPath[index]);
    const element = elementId
      ? Expressions.getElementById(render, elementId)
      : null;
    if (!element) return null;
    if (index === nativeIdPath.length - 1) return componentType;

    const nextId = getGuidNullable(nativeIdPath[index + 1]);
    const componentRoot = getComponentInstRootElement(componentMap)(element);
    if (
      nextId &&
      componentRoot &&
      Expressions.getElementById(EElement(componentRoot), nextId)
    ) {
      const nestedComponent = componentMap.get(element.type);
      if (!nestedComponent) return null;
      componentType = element.type;
      render = Component.getRender(nestedComponent);
    }
  }
  return null;
};

const getElementInComponent = (
  componentMap: Component.ComponentMap,
  componentType: Plugins.QualifiedElement | null,
  elementId: string
): ExpressionElement | null => {
  if (!componentType) return null;
  const component = componentMap.get(componentType);
  if (!component) return null;
  return Expressions.getElementById(Component.getRender(component), elementId);
};

export const resolveSlotDefaultMoveSource = ({
  sourceNativeIdPath,
  componentMap,
  plugins,
}: {
  sourceNativeIdPath: NativeId[];
  componentMap: Component.ComponentMap;
  plugins: Plugins.PluginsType;
}): SlotDefaultMoveSource | null => {
  const resolution = resolveSlotDefaultPath(
    sourceNativeIdPath,
    componentMap,
    plugins
  );
  if (!resolution) return null;

  const {element, componentInstance} = resolution.target;
  if (!isSlot(element)) return {element, nativeIdPath: sourceNativeIdPath};

  const componentRootPath = resolution.componentBoundary?.componentRootPath;
  return componentInstance && componentRootPath
    ? {element: componentInstance, nativeIdPath: componentRootPath}
    : null;
};

const resolvePromotionLevelsUncached = (
  sourceNativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): SlotDefaultLevel[] | null => {
  const source = resolveSlotDefaultMoveSource({
    sourceNativeIdPath,
    componentMap,
    plugins,
  });
  if (!source) return null;

  return resolveSlotDefaultWriteChain(
    source.nativeIdPath,
    componentMap,
    plugins
  );
};

// Callers need the outermost level to work out where the write lands before they
// can ask for a plan, so the chain resolves on its own and the plan reuses it.
const memoizedResolvePromotionLevels = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      weakMemo((sourceNativeIdPath: NativeId[]) =>
        resolvePromotionLevelsUncached(
          sourceNativeIdPath,
          componentMap,
          plugins
        )
      )
    )
);

const resolvePromotionLevels = ({
  sourceNativeIdPath,
  componentMap,
  plugins,
}: {
  sourceNativeIdPath: NativeId[];
  componentMap: Component.ComponentMap;
  plugins: Plugins.PluginsType;
}): SlotDefaultLevel[] | null =>
  memoizedResolvePromotionLevels(plugins)(componentMap)(sourceNativeIdPath);

/**
 * Normalizes projected source and target paths into one writable ComponentMap.
 * Instance moves become explicit overrides. Moving nested default content out
 * while editing its definition writes the empty override to the authored
 * instance in the shared default and returns the removed element as detached.
 */
/**
 * Where the moved element goes: a list inside the expression being written, and
 * a position in that list.
 *
 * Resolved once, while the promotion still knows which tree it is looking at.
 * The write then inserts, instead of searching the expression for an anchor id
 * that two projected trees can both answer for.
 */
export type SlotDefaultInsertion = {
  listPath: ExpressionPath;
  index: number;
};

/**
 * Places an element at a resolved insertion point.
 *
 * The list may not be there yet — an instance carries no override for a field
 * until something is written into it — so the path is filled in rather than
 * updated in place.
 */
export const applySlotDefaultInsertion = (
  expression: Expression,
  element: ExpressionElement,
  {listPath, index}: SlotDefaultInsertion
): Expression => {
  const listExpression = Expressions.getIn(expression, listPath);
  const list = listExpression
    ? (Expressions.getList(listExpression) ?? [])
    : [];
  const nextList = EList([
    ...list.slice(0, index),
    EElement(element),
    ...list.slice(index),
  ]);

  if (!listPath.length) return nextList;
  return Expressions.sparseSetIn(expression, listPath, nextList);
};

/**
 * Reads the insertion point a drop resolves to inside a slot default.
 *
 * A drop aimed at a Slot lands at one end of the field's list. An append or
 * prepend anywhere else lands inside the anchor, and a before or after lands
 * beside it in the list already holding it.
 */
const resolveSlotDefaultInsertion = (
  expression: Expression,
  anchorId: string,
  anchorPosition: RelativePositionPlacementOptions,
  anchorPath: ExpressionPath | null
): SlotDefaultInsertion | null => {
  const isLeading = anchorPosition === 'prepend' || anchorPosition === 'before';

  const anchorExpressionPath = Expressions.findPath(
    expression,
    (expr) => Expressions.getElement(expr)?.id === anchorId
  );

  // Nothing in a Slot's own default is the Slot, so a drop onto one has no
  // anchor in here to sit beside. The expression is the list, and the drop
  // lands at whichever end of it was being aimed for — including when it is
  // empty, which is just both ends at once.
  if (!anchorExpressionPath) {
    const list = Expressions.getList(expression);
    if (!list) return null;
    if (isLeading) return {listPath: [], index: 0};
    return {listPath: [], index: list.length};
  }

  // Inside the anchor: a Slot anchor names the field to land in, and anything
  // else takes the anchor's own children, which is what the drag engine means
  // by an append or prepend carrying no path.
  if (anchorPosition === 'append' || anchorPosition === 'prepend') {
    const listPath: ExpressionPath = [
      ...anchorExpressionPath,
      {in: 'Element'},
      ...(anchorPath ?? CHILDREN_PATH),
    ];
    const listExpression = Expressions.getIn(expression, listPath);
    const list = listExpression ? Expressions.getList(listExpression) : null;
    if (isLeading) return {listPath, index: 0};
    return {listPath, index: list?.length ?? 0};
  }

  // Beside the anchor: the list is the one holding it, and the anchor's own hop
  // says where in it that is.
  const anchorHop = anchorExpressionPath[anchorExpressionPath.length - 1];
  if (anchorHop?.in !== 'List') return null;

  const listPath = anchorExpressionPath.slice(0, -1);
  if (isLeading) return {listPath, index: anchorHop.index};
  return {listPath, index: anchorHop.index + 1};
};

/**
 * Everything a move accumulates as it is prepared, threaded through the phases
 * below. Each one takes a draft and returns the next, or null to abandon the
 * move entirely.
 */
type MoveDraft = {
  componentMap: Component.ComponentMap;
  source: PlacedElement;
  anchor: PlacedElement;
  detachedSource: ExpressionElement | null;
  anchorElement: ExpressionElement | null;
  anchorDestination: AnchorDestination | null;
};

/**
 * Lifts a live source out of the render of the definition being edited.
 *
 * A drop can land beside projected content rather than on the Slot, and that
 * anchor carries no field path — the target being projected is what says the
 * source has to come out of the definition's render.
 */
const detachLiveSourceFromDefinition = (
  draft: MoveDraft,
  {sourceElementId, editingComponentType}: SlotDefaultMoveInput,
  targetIsProjected: boolean
): MoveDraft => {
  if (draft.source.nativeIdPath) return draft;
  if (!targetIsProjected || !editingComponentType) return draft;

  const editingComponent = draft.componentMap.get(editingComponentType);
  if (!editingComponent) return draft;

  const render = Component.getRender(editingComponent);
  const sourceElement = Expressions.getElementById(render, sourceElementId);
  if (!sourceElement) return draft;

  return {
    ...draft,
    componentMap: draft.componentMap.set(
      editingComponentType,
      Component.update(editingComponent, {
        render: removeElementById(render, sourceElementId),
      })
    ),
    detachedSource: sourceElement,
  };
};

/**
 * Forks the chain the source sits in and lifts the source out of it.
 *
 * What moves travels detached from here on, because the mover has no way to
 * remove an element from a default once the fork has been written.
 */
const promoteSourceChain = (
  draft: MoveDraft,
  {plugins, editingComponentType}: SlotDefaultMoveInput,
  preparation: SlotDefaultMovePreparation,
  targetIsProjected: boolean
): MoveDraft | null => {
  const sourceNativeIdPath = draft.source.nativeIdPath;
  if (
    !sourceNativeIdPath ||
    !resolveSlotDefaultPathContext(
      sourceNativeIdPath,
      draft.componentMap,
      plugins
    )
  ) {
    return draft;
  }

  const levels = resolvePromotionLevels({
    sourceNativeIdPath,
    componentMap: draft.componentMap,
    plugins,
  });
  const outermost = levels?.[0];
  const authoredSourceId = levels && last(levels)?.elementId;
  if (!levels || !outermost || !authoredSourceId) return null;

  // Persisting the removal to a shared default while the pointer is still over
  // another projected target would empty that default on every frame, so a
  // preview forks an override instead of editing the definition it came from.
  let editsDefinition: Plugins.QualifiedElement | null = null;
  if (preparation === 'commit' || !targetIsProjected) {
    editsDefinition = editingComponentType ?? null;
  }

  const normalized = normalizeSlotDefaultChain(
    plugins,
    draft.componentMap,
    levels,
    editsDefinition,
    getOwningComponentType
  );
  if (!normalized) return null;

  const sourceId = normalized.idMap[authoredSourceId] ?? authoredSourceId;
  const movedElement = Expressions.getElementById(
    normalized.expression,
    sourceId
  );
  if (!movedElement) return null;

  const withoutSource = applySlotDefaultWrite(
    normalized.componentMap,
    normalized.location,
    removeElementById(normalized.expression, sourceId)
  );
  if (!withoutSource) return null;

  return {
    ...draft,
    componentMap: withoutSource,
    anchor: remapPlaced(
      draft.anchor,
      outermost.ownerInstancePath,
      normalized.idMap,
      normalized.expression
    ),
    // The source is out of the default now and travels detached, so it is no
    // longer reachable by a path.
    source: {id: sourceId, nativeIdPath: null},
    detachedSource: movedElement,
  };
};

/**
 * The first element of the default a Slot renders.
 *
 * A Slot is never in the default it renders, so a write aimed at one has to
 * aim at something inside it instead.
 */
const getFirstSlotDefaultElement = (
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  owner: ExpressionElement | null,
  anchorPath: ExpressionPath | null
): ExpressionElement | null => {
  const slotFieldId = getTargetedSlotFieldId(anchorPath);
  if (!owner || !slotFieldId) return null;

  const effectiveDefault = getEffectiveSlotDefaultExpression(
    plugins,
    componentMap,
    owner.type,
    slotFieldId,
    owner
  );
  if (!effectiveDefault) return null;

  const first = Expressions.getList(effectiveDefault)?.[0];
  if (!first) return null;
  return Expressions.getElement(first);
};

/**
 * The path whose chain the anchor's write has to go through.
 *
 * An anchor on a Slot aims at the first thing inside it, since the Slot itself
 * is not in there. Anything else is already in the default, and a path that
 * ends at an instance's render root names that instance one segment back.
 */
const resolveAnchorPromotionPath = (
  anchorNativeIdPath: NativeId[],
  target: SlotDefaultMoveSource | null,
  owner: ExpressionElement | null,
  firstDefaultElement: ExpressionElement | null
): NativeId[] | null => {
  if (owner && firstDefaultElement) {
    const ownerIndex = anchorNativeIdPath.reduce(
      (match, nativeId, index) =>
        getGuidNullable(nativeId) === owner.id ? index : match,
      -1
    );
    if (ownerIndex >= 0) {
      return [
        ...anchorNativeIdPath.slice(0, ownerIndex + 1),
        firstDefaultElement.id,
      ];
    }
  }

  if (!target) return null;
  if (target.element.id === getGuidNullable(anchorNativeIdPath.at(-1))) {
    return target.nativeIdPath;
  }
  return target.nativeIdPath.slice(0, -1);
};

/**
 * Forks the chain the anchor sits in, and reports where the write lands.
 *
 * The anchor comes back out of the fork rather than off the remapped path: a
 * forked level is an instance override, which the page walkers cannot follow.
 */
const promoteAnchorChain = (
  draft: MoveDraft,
  {plugins, anchorPath, editingComponentType}: SlotDefaultMoveInput,
  preparation: SlotDefaultMovePreparation
): MoveDraft | null => {
  const anchorNativeIdPath = draft.anchor.nativeIdPath;
  if (
    !anchorNativeIdPath ||
    !resolveSlotDefaultPathContext(
      anchorNativeIdPath,
      draft.componentMap,
      plugins,
      anchorPath
    )
  ) {
    return draft;
  }

  // Projected content is in no render, so the page walkers stop at the last
  // instance they can reach — a Slot nested inside a default would resolve to
  // whatever instance is holding that default. The projection-aware walk
  // answers for those, and declines a path that never leaves the render, so
  // the page walkers still answer for a Slot sitting in one.
  const anchorResolution = resolveSlotDefaultPath(
    anchorNativeIdPath,
    draft.componentMap,
    plugins
  );
  const targetElement =
    anchorResolution?.target.element ??
    getTargetElementOrInstance(anchorNativeIdPath, draft.componentMap, plugins);
  const target = resolveSlotDefaultMoveSource({
    sourceNativeIdPath: anchorNativeIdPath,
    componentMap: draft.componentMap,
    plugins,
  });

  let owner: ExpressionElement | null = null;
  if (targetElement && isSlot(targetElement)) {
    owner =
      anchorResolution?.target.componentInstance ??
      getInnermostSymbolInstance(
        anchorNativeIdPath,
        draft.componentMap,
        plugins
      );
  }

  const targetPath = resolveAnchorPromotionPath(
    anchorNativeIdPath,
    target,
    owner,
    getFirstSlotDefaultElement(plugins, draft.componentMap, owner, anchorPath)
  );
  let levels: SlotDefaultLevel[] | null = null;
  if (targetPath && targetPath.length > 1) {
    levels = resolvePromotionLevels({
      sourceNativeIdPath: targetPath,
      componentMap: draft.componentMap,
      plugins,
    });
  }
  const outermost = levels?.[0];
  if (!levels || !outermost) return null;

  // A source that is still attached has to stay reachable, so the anchor forks
  // an override rather than editing the definition both ends live in.
  let editsDefinition: Plugins.QualifiedElement | null = null;
  if (!draft.source.nativeIdPath && preparation === 'commit') {
    editsDefinition = editingComponentType ?? null;
  }

  const normalized = normalizeSlotDefaultChain(
    plugins,
    draft.componentMap,
    levels,
    editsDefinition,
    getOwningComponentType
  );
  if (!normalized) return null;

  const remapArgs = [
    outermost.ownerInstancePath,
    normalized.idMap,
    normalized.expression,
  ] as const;
  let anchor = remapPlaced(draft.anchor, ...remapArgs);

  // A Slot answers with the instance that owns it; anything else is already the
  // element aimed at. A Slot the definition itself owns has no owner in the
  // fork, and stays put.
  let anchorElement = draft.anchorElement;
  const anchorIdInChain = owner
    ? (normalized.idMap[owner.id] ?? owner.id)
    : anchor.id;
  const anchorElementInChain = Expressions.getElementById(
    normalized.expression,
    anchorIdInChain
  );
  if (anchorElementInChain) {
    anchor = {...anchor, id: anchorIdInChain};
    anchorElement = anchorElementInChain;
  }

  let anchorDestination: AnchorDestination;
  if (normalized.location.kind === 'componentDefault') {
    anchorDestination = {
      kind: 'slotDefault',
      componentType: normalized.location.componentType,
      fieldId: normalized.location.fieldId,
      expression: normalized.expression,
    };
  } else {
    anchorDestination = {
      kind: 'componentRender',
      componentType: normalized.location.componentType,
    };
  }

  return {
    ...draft,
    componentMap: normalized.componentMap,
    source: remapPlaced(draft.source, ...remapArgs),
    anchor,
    anchorElement,
    anchorDestination,
  };
};

/**
 * Resolves an anchor the promotion never touched, by walking for it.
 *
 * Only runs when no chain was forked, so the render is the whole story here.
 */
const resolveAnchorInRender = (
  draft: MoveDraft,
  {plugins, anchorPath, editingComponentType}: SlotDefaultMoveInput
): MoveDraft | null => {
  const anchorNativeIdPath = draft.anchor.nativeIdPath;
  if (!anchorNativeIdPath || draft.anchorElement) return draft;

  const walkedAnchor = getTargetElementOrInstance(
    anchorNativeIdPath,
    draft.componentMap,
    plugins
  );
  let anchor = draft.anchor;
  if (walkedAnchor) anchor = {...anchor, id: walkedAnchor.id};

  if (!anchorPath || !walkedAnchor || !isSlot(walkedAnchor)) {
    return {...draft, anchor, anchorElement: walkedAnchor};
  }

  // A Slot belonging to the component being edited has no owning instance
  // inside the definition — the instance that owns it sits on the page, outside
  // anything this write touches — so the Slot stays the anchor and the mover
  // resolves its field. A Slot on a nested instance does have one, and that
  // instance is inside the expression being written.
  const editingComponent = editingComponentType
    ? draft.componentMap.get(editingComponentType)
    : null;
  const ownsFocusedDefinitionSlot = Boolean(
    editingComponent &&
    getComponentSlots(editingComponent).some(
      (slot) => slot.id === walkedAnchor.id
    )
  );

  // A Slot the definition itself owns has no owning instance in here; the
  // insertion resolves against the default it renders instead.
  if (ownsFocusedDefinitionSlot) {
    return {...draft, anchor, anchorElement: walkedAnchor};
  }

  const owner = getInnermostSymbolInstance(
    anchorNativeIdPath,
    draft.componentMap,
    plugins
  );
  if (!owner) return null;

  let anchorDestination = draft.anchorDestination;
  if (!anchorDestination) {
    const ownerIndex = anchorNativeIdPath.findIndex(
      (nativeId) => getGuidNullable(nativeId) === owner.id
    );
    const componentType = getOwningComponentType(
      draft.componentMap,
      anchorNativeIdPath.slice(0, ownerIndex + 1)
    );
    if (!componentType) return null;
    anchorDestination = {kind: 'componentRender', componentType};
  }

  return {
    ...draft,
    anchor: {...anchor, id: owner.id},
    anchorElement: owner,
    anchorDestination,
  };
};

/**
 * The anchor element the caller gets, read out of whatever now holds it.
 */
const readAnchorElement = (draft: MoveDraft): ExpressionElement | null => {
  const {anchorDestination, anchor, anchorElement} = draft;
  if (anchorDestination?.kind === 'slotDefault') {
    return (
      Expressions.getElementById(anchorDestination.expression, anchor.id) ??
      anchorElement
    );
  }
  if (anchorElement?.id === anchor.id) return anchorElement;

  return getElementInComponent(
    draft.componentMap,
    anchorDestination?.componentType ?? null,
    anchor.id
  );
};

/** The insertion point the finished draft resolves to. */
const resolveInsertion = (
  draft: MoveDraft,
  {anchorPath, anchorPosition}: SlotDefaultMoveInput
): SlotDefaultInsertion | null => {
  if (draft.anchorDestination?.kind !== 'slotDefault') return null;
  if (!anchorPosition) return null;

  return resolveSlotDefaultInsertion(
    draft.anchorDestination.expression,
    draft.anchor.id,
    anchorPosition,
    anchorPath
  );
};

const prepareSlotDefaultMoveUncached = (
  input: SlotDefaultMoveInput,
  preparation: SlotDefaultMovePreparation
): PromotedSlotDefaultMove | null => {
  const {
    componentMap,
    plugins,
    sourceElementId,
    sourceNativeIdPath,
    anchorElementId,
    anchorNativeIdPath,
    anchorPath,
  } = input;

  // Whether the drop is aimed at projected content decides both whether a live
  // source has to leave the definition's render and, on a preview, whether the
  // source's removal is allowed to touch a shared default.
  const targetIsProjected = Boolean(
    anchorNativeIdPath &&
    resolveSlotDefaultPathContext(
      anchorNativeIdPath,
      componentMap,
      plugins,
      anchorPath
    )
  );

  const start: MoveDraft = {
    componentMap,
    source: {id: sourceElementId, nativeIdPath: sourceNativeIdPath},
    anchor: {id: anchorElementId, nativeIdPath: anchorNativeIdPath},
    detachedSource: null,
    anchorElement: null,
    anchorDestination: null,
  };

  const detached = detachLiveSourceFromDefinition(
    start,
    input,
    targetIsProjected
  );
  const withSource = promoteSourceChain(
    detached,
    input,
    preparation,
    targetIsProjected
  );
  if (!withSource) return null;

  const withAnchor = promoteAnchorChain(withSource, input, preparation);
  if (!withAnchor) return null;

  const draft = resolveAnchorInRender(withAnchor, input);
  if (!draft) return null;

  return {
    componentMap: draft.componentMap,
    sourceElementId: draft.source.id,
    sourceNativeIdPath: draft.source.nativeIdPath,
    detachedSourceElement: draft.detachedSource,
    anchorElement: readAnchorElement(draft),
    anchorElementId: draft.anchor.id,
    anchorNativeIdPath: draft.anchor.nativeIdPath,
    anchorDestination: draft.anchorDestination,
    insertion: resolveInsertion(draft, input),
  };
};

/**
 * One promotion per (map, placement, preparation) — hover validation, the drag
 * preview and the commit all ask for the same fork, and building it walks the
 * write chain, rebuilds the nested default and re-mints every id in it. Paths
 * arrive as fresh arrays each frame, so the cache compares them by value; the
 * ComponentMap and plugins stay reference keys, and the cache dies with them.
 */
const memoizedPrepareSlotDefaultMove = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      memoize(
        (
          preparation: SlotDefaultMovePreparation,
          sourceElementId: string,
          sourceNativeIdPath: NativeId[] | null,
          anchorElementId: string,
          anchorNativeIdPath: NativeId[] | null,
          anchorSlotFieldId: string,
          anchorPosition: RelativePositionPlacementOptions | null,
          editingComponentType: Plugins.QualifiedElement | null
        ) =>
          prepareSlotDefaultMoveUncached(
            {
              componentMap,
              plugins,
              sourceElementId,
              sourceNativeIdPath,
              anchorElementId,
              anchorNativeIdPath,
              anchorPath: anchorSlotFieldId
                ? [{in: 'Record', at: anchorSlotFieldId}]
                : null,
              anchorPosition,
              editingComponentType,
            },
            preparation
          ),
        8
      )
    )
);

const prepareSlotDefaultMove = (
  input: SlotDefaultMoveInput,
  preparation: SlotDefaultMovePreparation
): PromotedSlotDefaultMove | null => {
  const anchorSlotFieldId = getTargetedSlotFieldId(input.anchorPath);
  // Anything but the canonical single-hop Slot anchor can't be rebuilt from the
  // cache key, so it skips the cache rather than being approximated.
  if (input.anchorPath && !anchorSlotFieldId) {
    return prepareSlotDefaultMoveUncached(input, preparation);
  }

  return memoizedPrepareSlotDefaultMove(input.plugins)(input.componentMap)(
    preparation,
    input.sourceElementId,
    input.sourceNativeIdPath,
    input.anchorElementId,
    input.anchorNativeIdPath,
    anchorSlotFieldId ?? '',
    input.anchorPosition ?? null,
    input.editingComponentType ?? null
  );
};

export const promoteSlotDefaultMove = (
  input: SlotDefaultMoveInput
): PromotedSlotDefaultMove | null => prepareSlotDefaultMove(input, 'preview');

export const prepareSlotDefaultMoveCommit = (
  input: SlotDefaultMoveInput
): PromotedSlotDefaultMove | null => prepareSlotDefaultMove(input, 'commit');
